// Route detail card -- renders when a row is selected from
// LogisticsRoutesList. Shows the route header (state chip + carrier
// + schedule), the ordered stops timeline, and delegates the
// add-stop form to LogisticsRoutesAddStopForm (extracted in the D3e
// UI prep split so the file stays under the soft-warning band).
import { Fragment } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  ROUTE_STATE_ACTION_LABELS,
  ROUTE_STATE_CHIP_CLASSES,
  ROUTE_STATE_LABELS,
  carrierLabel,
  dangerBtnClass,
  formatRouteScheduled,
  formatTimestamp,
  ghostBtnClass,
  primaryBtnClass,
  unitLabel,
  type LogisticsRoutesLabelLookup,
} from "./logisticsRoutesShared";
import { LogisticsRoutesAddStopForm } from "./LogisticsRoutesAddStopForm";
import { LogisticsRoutesAssignmentEditForm } from "./LogisticsRoutesAssignmentEditForm";
import { LogisticsRouteEmailManifestButton } from "./LogisticsRouteEmailManifestButton";
import { LogisticsRoutesStopEditForm } from "./LogisticsRoutesStopEditForm";
import { LogisticsRoutesStopRow } from "./LogisticsRoutesStopRow";
import { useLogisticsRouteAssignmentEdit } from "./useLogisticsRouteAssignmentEdit";
import { useLogisticsRouteDetail } from "./useLogisticsRouteDetail";
import { useLogisticsRouteStateTransitions } from "./useLogisticsRouteStateTransitions";
import { useLogisticsRouteStopEdits } from "./useLogisticsRouteStopEdits";
import { useLogisticsRouteStopReorder } from "./useLogisticsRouteStopReorder";
import type { LogiRouteState } from "@/lib/logistics";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  listModel: LogisticsRoutesLabelLookup;
  routeId: string;
  // Bumped whenever the list refreshes so the detail refetches too.
  // After a state transition, the parent bumps both this AND its own
  // list-nav tick so the chip color updates in the route list too.
  detailNavTick: number;
  // Invoked after a successful state transition so the panel can
  // refresh the route LIST view (chip color, terminal-state filtering).
  // Detail refresh is handled by the hook via its own reload().
  onAfterStateChange?: () => void;
  onClose: () => void;
};

export function LogisticsRoutesDetailCard({
  listModel,
  routeId,
  detailNavTick,
  onAfterStateChange,
  onClose,
}: Props) {
  const d = useLogisticsRouteDetail(routeId, detailNavTick);
  // State-transition affordance lives in its own sibling hook (D3e UI
  // prep split for D3g UI). Composes against the detail hook's
  // current route_state + reload.
  const t = useLogisticsRouteStateTransitions(
    routeId,
    d.detail?.route.route_state ?? null,
    d.reload
  );
  // D3g UI: per-stop quick actions + admin edit form. Takes the
  // current stops slice from the detail hook so the submitEdit
  // diff has the latest stored values to compare against.
  const e = useLogisticsRouteStopEdits(
    routeId,
    d.detail?.stops ?? [],
    d.reload
  );
  // D3j: rig + driver reassignment ("the driver can be overwritten at
  // time of scheduling"). Disabled when the route has no carrier yet --
  // a rig can't ride without a carrier.
  const a = useLogisticsRouteAssignmentEdit(d.detail?.route ?? null, d.reload);
  const rOrder = useLogisticsRouteStopReorder(routeId, d.detail?.stops ?? [], d.reload);

  if (d.loading) {
    return (
      <SectionCard title="Selected route">
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading route detail…</div>
      </SectionCard>
    );
  }
  if (d.error) {
    return (
      <SectionCard title="Selected route">
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {d.error}
        </div>
        <div className="mt-2">
          <button type="button" className={ghostBtnClass} onClick={onClose}>
            Close
          </button>
        </div>
      </SectionCard>
    );
  }
  if (!d.detail) return null;
  const { route, stops } = d.detail;
  const chipClass =
    ROUTE_STATE_CHIP_CLASSES[route.route_state] ?? "bg-gray-500 text-white";

  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "logi-route-detail" }}>
    <SectionCard title="Selected route">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
            <code>{unitLabel(listModel.units, route.unit_id)}</code>
            <span
              className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${chipClass}`}
            >
              {ROUTE_STATE_LABELS[route.route_state] ?? route.route_state}
            </span>
          </div>
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Carrier: {carrierLabel(listModel.carriers, route.carrier_id)}
          </div>
          {/* D3j: rig + per-run driver. Both nullable; rig has no carrier
              means it's still rotating-driver-tentative. The Edit button
              opens the inline reassign form (sibling component below). */}
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Rig:{" "}
            {route.rig_id ? (
              <code>{route.rig_id.slice(0, 8)}</code>
            ) : (
              <span className="italic text-gray-500 dark:text-zinc-500">— none assigned</span>
            )}
          </div>
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Driver:{" "}
            {route.driver_name ? (
              route.driver_name
            ) : (
              <span className="italic text-gray-500 dark:text-zinc-500">
                — write in at scheduling
              </span>
            )}
          </div>
          {formatRouteScheduled(route) ? (
            <div className="text-xs text-gray-600 dark:text-zinc-400">
              Scheduled: {formatRouteScheduled(route)}
            </div>
          ) : null}
          {route.completed_at ? (
            <div className="text-xs text-gray-600 dark:text-zinc-400">
              Completed: {formatTimestamp(route.completed_at)}
            </div>
          ) : null}
          {route.notes ? (
            <div className="text-xs text-gray-600 dark:text-zinc-400">{route.notes}</div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {a.canEdit && !a.editing ? (
            <button
              type="button"
              className={ghostBtnClass}
              onClick={a.startEdit}
              disabled={d.adding}
            >
              Edit rig / driver
            </button>
          ) : null}
          <LogisticsRouteEmailManifestButton
            routeId={route.id}
            defaultEmail={
              route.carrier_id
                ? listModel.carriers.find((c) => c.id === route.carrier_id)?.contact_email ?? null
                : null
            }
          />
          <button
            type="button"
            className={primaryBtnClass}
            onClick={d.startAdd}
            disabled={d.adding}
          >
            + Add stop
          </button>
          <button type="button" className={ghostBtnClass} onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {a.editing ? <LogisticsRoutesAssignmentEditForm a={a} /> : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500">
          State:
        </span>
        {t.allowedTransitions.length === 0 ? (
          <span className="text-xs italic text-gray-500 dark:text-zinc-500">
            terminal — no further transitions
          </span>
        ) : (
          t.allowedTransitions.map((next) => {
            const isCancel = next === "cancelled";
            const cls = isCancel ? dangerBtnClass : ghostBtnClass;
            const label = ROUTE_STATE_ACTION_LABELS[next] ?? `Mark ${next}`;
            const busy = t.transitionBusy === next;
            return (
              <button
                key={next}
                type="button"
                className={cls}
                disabled={t.transitionBusy !== null}
                onClick={() =>
                  void t.submitTransition(next as LogiRouteState, onAfterStateChange)
                }
              >
                {busy ? `${label}…` : label}
              </button>
            );
          })
        )}
      </div>
      {t.transitionError ? (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {t.transitionError}
        </div>
      ) : null}

      <div
        {...{ [FIREFLY_TARGET_ATTR]: "logi-route-stops" }}
        className="mt-4"
      >
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500 mb-2">
          Stops (ordered — drag or use arrows to reorder pickup sequence)
        </div>
        {rOrder.reorderError ? (
          <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {rOrder.reorderError}
          </div>
        ) : null}
        {e.quickActionError ? (
          <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {e.quickActionError}
          </div>
        ) : null}
        {stops.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-zinc-400">
            No stops yet. Add the plant origin first, then optional dealer-holding legs, then the
            end-customer drop.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
            {stops.map((s, idx) => (
              <Fragment key={s.id}>
                <LogisticsRoutesStopRow
                  stop={s}
                  isEditing={e.editingStopId === s.id}
                  quickActionBusy={e.quickActionBusy}
                  onMarkArrived={() => void e.submitMarkArrived(s)}
                  onMarkDeparted={() => void e.submitMarkDeparted(s)}
                  onCaptureEvidence={(file) => void e.submitCaptureEvidence(s, file)}
                  onStartEdit={() => e.startEdit(s)}
                  reorder={{
                    canReorder: rOrder.canReorder,
                    reorderBusy: rOrder.reorderBusy,
                    isFirst: idx === 0,
                    isLast: idx === stops.length - 1,
                    onMoveUp: () => rOrder.moveStop(s.id, -1),
                    onMoveDown: () => rOrder.moveStop(s.id, 1),
                    onDragStart: () => rOrder.setDragStopId(s.id),
                    onDragOver: (ev) => {
                      ev.preventDefault();
                    },
                    onDrop: () => rOrder.dropOnStop(s.id),
                  }}
                />
                {e.editingStopId === s.id ? (
                  <li className="py-2">
                    <LogisticsRoutesStopEditForm e={e} />
                  </li>
                ) : null}
              </Fragment>
            ))}
          </ul>
        )}
      </div>

      <LogisticsRoutesAddStopForm d={d} />
    </SectionCard>
    </div>
  );
}
